
import { computed, reactive } from "vue";

// ============================================================
// STATE
// ============================================================

const state = reactive({
  // ==========================================================
  // TERMINAL / SESSION
  // ==========================================================
  terminals: [],
  terminal: null,
  session: null,
  loadingTerminals: false,
  loadingSession: false,
  sessionBusy: false,
  sessionError: null,
  terminalError: null,

  // ==========================================================
  // CATALOG
  // ==========================================================
  products: [],
  categories: [],
  selectedCategoryId: null,
  searchText: "",
  loadingProducts: false,
  loadingCategories: false,

  // ==========================================================
  // CART
  // ==========================================================
  cart: [],

  // ==========================================================
  // CUSTOMER / LOYALTY
  // ==========================================================
  customer: null,

  // Loyalty membership is independent of POS customer.
  membershipId: null,
  loyaltyProgramId: null,
  membership: null,

  // ==========================================================
  // CURRENCY
  // ==========================================================
  currency: null,
  offlineInvoiceId: null,

  // ==========================================================
  // DISCOUNT
  // ==========================================================
  discountRate: null,
  discountAmount: null,

  // ==========================================================
  // TAX
  // ==========================================================
  taxCodes: [],
  taxCodeId: null,
  taxCode: null,
  loadingTaxes: false,

  // ==========================================================
  // TIP
  // ==========================================================
  tipRate: null,
  tipAmount: null,

  // ==========================================================
  // TRANSACTION IDENTITY
  //
  // One ClientTransactionId belongs to one sale only.
  // It may survive refresh/retry while that sale is active.
  // ==========================================================
  clientTransactionId: null,

  // ==========================================================
  // SALE
  // ==========================================================
  sale: null,
  creatingSale: false,

  // ==========================================================
  // CHECKOUT
  // ==========================================================
  checkout: null,
  checkoutDialog: false,
  checkingOut: false,

  // ==========================================================
  // PAYMENT
  // ==========================================================
  paymentMethod: null,
  paymentTransaction: null,
  paymentStatus: null,
  paymentError: null,
  processingPayment: false,

  // ==========================================================
  // STRIPE
  // ==========================================================
  stripeCheckout: null,
  stripeCheckoutSessionId: null,
  stripePaymentTransactionId: null,
  stripePolling: false,

  // ==========================================================
  // RECEIPT
  // ==========================================================
  receipt: null,

  // ==========================================================
  // GENERAL ERROR
  // ==========================================================
  error: null
});

// ============================================================
// APPLY ACTIVE SESSION CURRENCY
// ============================================================

function applySessionCurrency(session) {
  const currency = String(session?.currencyCode ?? "")
    .trim()
    .toUpperCase();

  if (!/^[A-Z]{3}$/.test(currency)) {
    throw new Error(
      "The active POS session has no valid currency."
    );
  }

  state.currency = currency;
  return currency;
}

// ============================================================
// COMPUTED STATE
// ============================================================

const cartItemCount = computed(() =>
  state.cart.reduce(
    (total, item) =>
      total + Number(item.quantity ?? 0),
    0
  )
);

const cartSubtotal = computed(() =>
  state.cart.reduce(
    (total, item) =>
      total +
      Number(item.unitPrice ?? 0) *
      Number(item.quantity ?? 0),
    0
  )
);

const hasItems = computed(() =>
  state.cart.length > 0
);

const hasSale = computed(() =>
  Boolean(state.sale?.id)
);

const backendTotal = computed(() =>
  Number(
    state.sale?.totalAmount ??
    cartSubtotal.value
  )
);

const outstandingAmount = computed(() =>
  Number(
    state.sale?.outstandingAmount ??
    backendTotal.value
  )
);

const hasTerminal = computed(() =>
  Boolean(
    state.terminal?.id ??
    state.terminal?.terminalId
  )
);

const hasActiveSession = computed(() =>
  Boolean(
    state.session?.id ??
    state.session?.sessionId
  )
);

// ============================================================
// TERMINAL SESSION POLICY
// ============================================================

function shouldPersistSession() {
  return state.terminal?.persistSession === true;
}

function shouldClearSessionAfterTransaction() {
  return (
    state.terminal?.clearSessionAfterTransaction === true
  );
}

// ============================================================
// RESET CURRENT TRANSACTION
//
// DOES NOT RESET:
// - Terminal
// - Session
// - Products
// - Categories
// - Tax catalog
// ============================================================

function resetTransaction() {
  // CART
  state.cart.splice(0, state.cart.length);
  state.offlineInvoiceId = null;

  // TRANSACTION IDENTITY
  // Next sale receives a new client transaction ID.
  state.clientTransactionId = null;

  // CUSTOMER / LOYALTY
  state.customer = null;
  state.membershipId = null;
  state.loyaltyProgramId = null;
  state.membership = null;

  // DISCOUNT
  state.discountRate = null;
  state.discountAmount = null;

  // TAX SELECTION
  state.taxCodeId = null;
  state.taxCode = null;

  // TIP
  state.tipRate = null;
  state.tipAmount = null;

  // SALE
  state.sale = null;
  state.creatingSale = false;

  // CHECKOUT
  state.checkout = null;
  state.checkoutDialog = false;
  state.checkingOut = false;

  // PAYMENT
  state.paymentMethod = null;
  state.paymentTransaction = null;
  state.paymentStatus = null;
  state.paymentError = null;
  state.processingPayment = false;

  // STRIPE
  state.stripeCheckout = null;
  state.stripeCheckoutSessionId = null;
  state.stripePaymentTransactionId = null;
  state.stripePolling = false;

  // RECEIPT
  state.receipt = null;

  // GENERAL ERROR
  state.error = null;
}

// ============================================================
// RESET SESSION
//
// Used after cashier session closure.
// Keeps terminal, terminal catalog, products and tax catalog.
// ============================================================

function resetSession() {
  resetTransaction();

  state.session = null;
  state.currency = null;
  state.loadingSession = false;
  state.sessionBusy = false;
  state.sessionError = null;
}

// ============================================================
// RESET TERMINAL
//
// Does not deactivate the backend terminal.
// ============================================================

function resetTerminal() {
  resetSession();

  state.terminal = null;
  state.terminalError = null;
  state.loadingTerminals = false;
}

// ============================================================
// RESET ALL
// ============================================================

function resetAll() {
  resetTerminal();

  state.terminals = [];
  state.products = [];
  state.categories = [];
  state.selectedCategoryId = null;
  state.searchText = "";
  state.loadingProducts = false;
  state.loadingCategories = false;

  state.taxCodes = [];
  state.loadingTaxes = false;

  state.currency = null;
  state.error = null;
  state.terminalError = null;
  state.sessionError = null;
}

// ============================================================
// EXPORT
// ============================================================

export default {
  state,
  applySessionCurrency,

  cartItemCount,
  cartSubtotal,
  hasItems,
  hasSale,
  backendTotal,
  outstandingAmount,
  hasTerminal,
  hasActiveSession,

  resetTransaction,
  resetSession,
  resetTerminal,
  resetAll,

  shouldPersistSession,
  shouldClearSessionAfterTransaction
};
